<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchAllQuizzes, updateQuiz } from "./api";

const route = useRoute(), router = useRouter();
const id = route.params.id;
const LANG_ITEMS = [{ title: "English", value: "en" }, { title: "Chinese", value: "zh" }];
const TYPE_ITEMS = [{ title: "Multiple Choice", value: "mc" }, { title: "Fill in the Blanks", value: "blanks" }];
const loading = ref(true), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
let originalQuiz = null;

const code = ref(""), level = ref(""), lang = ref("en"), type = ref("mc"), title = ref(""), enable = ref(false);
const competency = ref(""), text = ref(""), mcResponses = ref([""]), mcAnswer = ref(""), blanksSentence = ref(""), blanksAnswer = ref("");
const isMc = computed(() => type.value === "mc");

function populate(quiz) {
  originalQuiz = quiz;
  code.value = quiz.code ?? ""; level.value = Array.isArray(quiz.level) ? quiz.level.join(", ") : quiz.level ?? ""; lang.value = quiz.lang ?? "en"; type.value = quiz.type ?? "mc"; title.value = quiz.title ?? ""; enable.value = !!quiz.enable;
  const q = (quiz.questions && quiz.questions[0]) || {};
  competency.value = q.competency ?? ""; text.value = q.text ?? "";
  if (type.value === "mc") { mcResponses.value = Array.isArray(q.responses) ? [...q.responses] : q.responses ? [q.responses] : [""]; mcAnswer.value = Array.isArray(q.answer) ? q.answer.join("; ") : q.answer ?? ""; }
  else { blanksSentence.value = Array.isArray(q.responses) ? q.responses.join(" ") : q.responses ?? ""; blanksAnswer.value = Array.isArray(q.answer) ? q.answer.join("; ") : q.answer ?? ""; }
}

onMounted(async () => {
  try {
    const all = await fetchAllQuizzes();
    const quiz = all.find(q => q._id === id);
    if (!quiz) { notFound.value = true; return; }
    populate(quiz);
  } catch { error.value = "Couldn't load the quiz."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const lvl = level.value.split(",").map(s => s.trim()).filter(Boolean);
  let responses, answer;
  if (isMc.value) {
    responses = mcResponses.value.map(s => s.trim()).filter(Boolean);
    answer = mcAnswer.value.trim();
    if (responses.length === 0) { validationError.value = "Add at least one response option."; return; }
    if (!answer) { validationError.value = "Enter the correct answer."; return; }
    if (!responses.includes(answer)) { validationError.value = "The correct answer must exactly match one of the options."; return; }
  } else {
    responses = blanksSentence.value.trim();
    answer = blanksAnswer.value.split(";").map(s => s.trim()).filter(Boolean);
    if (!responses) { validationError.value = "Enter the sentence."; return; }
    if (answer.length === 0) { validationError.value = "Enter at least one correct answer."; return; }
  }
  if (originalQuiz && code.value !== originalQuiz.code) { validationError.value = "Code is read-only and cannot be changed."; return; }
  const orig = originalQuiz || {};
  const origQuestions = Array.isArray(orig.questions) ? orig.questions : [];
  const q0 = { ...(origQuestions[0] || {}), competency: competency.value, text: text.value, responses, answer, type: type.value };
  const payload = { lang: lang.value, level: lvl, title: title.value, type: type.value, enable: enable.value, questions: [q0, ...origQuestions.slice(1)] };
  saving.value = true;
  try { await updateQuiz(id, payload); success.value = true; setTimeout(() => router.push({ name: "quizzes" }), 800); }
  catch { error.value = "Couldn't save the quiz."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'quizzes' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Edit Quiz</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Quiz not found.</VAlert>
    <VCard v-else class="pa-4" max-width="720">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Quiz updated. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12"><VTextField v-model="code" label="Code" hint="Code is read-only." readonly disabled /></VCol>
          <VCol cols="12"><VTextField v-model="level" label="Level" hint="Separate multiple levels with commas." /></VCol>
          <VCol cols="12" md="6"><VSelect v-model="lang" label="Language" :items="LANG_ITEMS" item-title="title" item-value="value" /></VCol>
          <VCol cols="12" md="6"><VSelect v-model="type" label="Quiz Type" :items="TYPE_ITEMS" item-title="title" item-value="value" /></VCol>
          <VCol cols="12"><VTextField v-model="title" label="Title" /></VCol>
          <VCol cols="12"><VCheckbox v-model="enable" label="Enabled" hide-details /></VCol>
        </VRow>
        <VDivider class="my-4" />
        <div class="text-body-1 font-weight-semibold mb-4">Question</div>
        <VRow>
          <VCol cols="12"><VTextField v-model="competency" label="Competency" /></VCol>
          <VCol cols="12"><VTextField v-model="text" label="Question Text" /></VCol>
          <VCol v-if="isMc" cols="12">
            <div class="text-body-2 font-weight-semibold mb-2">Responses (Options)</div>
            <div v-for="(r, i) in mcResponses" :key="i" class="d-flex gap-2 mb-2">
              <VTextField v-model="mcResponses[i]" :label="`Option ${i + 1}`" hide-details class="flex-grow-1" />
              <VBtn icon="mdi:close" size="small" variant="text" color="error" @click="mcResponses.splice(i,1); if(!mcResponses.length) mcResponses.push('')" />
            </div>
            <VBtn variant="text" size="small" prepend-icon="mdi:plus" @click="mcResponses.push('')">Add Option</VBtn>
            <VTextField v-model="mcAnswer" label="Correct Answer" hint="Must match one of the options exactly." class="mt-3" />
          </VCol>
          <VCol v-else cols="12">
            <VTextField v-model="blanksSentence" label="Sentence (Responses)" hint="Use [brackets] to enclose the word to fill in." />
            <VTextField v-model="blanksAnswer" label="Correct Answer(s)" hint="Separate multiple answers with semicolons." class="mt-3" />
          </VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">Update</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'quizzes' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
